import { Grid, Typography } from '@mui/material';
import {
    ActionTypes,
    AdminRoleId,
    GridSettingsContext as IGridSettingsContext
} from '@tassat/common-definitions';
import { WithdrawalForm } from '@tassat/forms';
import { AccountWithdrawalDataRecord } from '@tassat/fred-definitions';
import { useSnackbar } from '@tassat/text';
import React from 'react';
import { Route } from 'react-router-dom';

import { arePendingTransactions, submitWithdrawalForm } from 'Containers/admin/forms/helpers/createWithdrawalInstructionFormFn';
import { exportWithdrawalFirmData } from 'Lib/exports/withdrawalExports';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole } from 'Selectors/common';
import { getWithdrawalInstructionsArray } from 'Selectors/withdrawalInstructions';
import SideListComponent from 'SharedComponents/layouts/sideListComponent';
import { SidelistLayoutFormOptions } from 'SharedComponents/layouts/sideListLayout';
import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout/gridSettingsContext';
import { SideListGridOptions } from 'SharedComponents/sideList';

import FirmWithdrawalDetails from './firmWithdrawalDetails';
import { cellHeight, firmWithdrawalColumnDefs } from './gridOptions';

import { IFirmWithdrawalProps } from '.';

export function FirmWithdrawal(props: IFirmWithdrawalProps) {
    const {
        classes,
        firm
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const withdrawalInstructions = useAppSelector((state) => getWithdrawalInstructionsArray(state, firm?.FirmId));
    const [ withdrawalInstructionsFormOpen, setWithdrawalInstructionsFormOpen ] = React.useState<boolean>(false);
    const [ editWithdrawalInstruction, setEditWithdrawalInstruction ] = React.useState<AccountWithdrawalDataRecord | null>(null);
    const [ formActionType, setFormActionType ] =
    React.useState<ActionTypes>(ActionTypes.add);
    const [ deleteWithdrawalInstruction, setDeleteWithdrawalInstruction ] = React.useState(null);
    const { showSnackbar } = useSnackbar();

    const editSelectedWithdrawalInstruction = (withdrawalInstruction, func) => {
        setDeleteWithdrawalInstruction(func);
        setEditWithdrawalInstruction(withdrawalInstruction);
        setFormActionType(ActionTypes.edit);
        setWithdrawalInstructionsFormOpen(true);
    };

    const closeWithdrawalInstructionForm = () => {
        setDeleteWithdrawalInstruction(null);
        setEditWithdrawalInstruction(null);
        setFormActionType(ActionTypes.add);
        setWithdrawalInstructionsFormOpen(false);
    };

    const layoutSettingsMemo: IGridSettingsContext = React.useMemo(() => {
        return {
            columnDefs: [],
            options: { cellHeight: 0 }
        };
    }, []);

    const gridOptions: SideListGridOptions = React.useMemo(() => ({
        idField: 'AccountWithdrawalDataId',
        headerHeight: 60,
        cellHeight: cellHeight,
        exportData: async () => {
            try {
                await exportWithdrawalFirmData(firm.FirmId);
            } catch (error) {
                showSnackbar('Unable to download, please try again later. If issue persists, contact support.', 'error');
            }
        }
    }), []);

    const formOptions: SidelistLayoutFormOptions = React.useMemo(() => ({
        formTitle: `${formActionType === ActionTypes.add ? 'Add New' : 'Edit'} Instruction`,
        Form: (userRole.AdminRoleId === AdminRoleId.BANK_OPS) ? WithdrawalForm : undefined,
        formModalWidth: '744px',
        showAddButton: !firm.IsClosed,
        formProps: {
            modalOpen: withdrawalInstructionsFormOpen,
            onClose: closeWithdrawalInstructionForm,
            actionType: formActionType,
            editWithdrawalInstruction: editWithdrawalInstruction,
            firmWithdrawalInstructions: withdrawalInstructions,
            deleteWithdrawalInstruction: deleteWithdrawalInstruction,
            firm: firm,
            features: {},
            arePendingTransactions,
            submitWithdrawalForm: (values, opts) => submitWithdrawalForm(values, opts)
        }
    }), [
        deleteWithdrawalInstruction,
        editWithdrawalInstruction,
        withdrawalInstructions,
        firm,
        formActionType,
        withdrawalInstructionsFormOpen,
        userRole
    ]);

    const noWithdrawalInstructionSelectedMessage = 'Select an instruction on the left to view or edit the instruction information';

    return (
        <GridSettingsContext.Provider value={layoutSettingsMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='flex-start'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.usersContainer}
                    container
                    direction='row'
                    item
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.usersGridContainer}
                        item
                        xs={4}
                    >
                        <SideListComponent
                            columnDefs={firmWithdrawalColumnDefs}
                            formOptions={formOptions}
                            gridOptions={gridOptions}
                            rowData={withdrawalInstructions}
                        />
                    </Grid>
                    <Grid
                        className={classes.selectedUserContainer}
                        item
                        xs={8}
                    >
                        <Route
                            exact
                            path='/admin/firms/:firmId/withdrawals/:accountWithdrawalDataId'
                            render={props => (
                                <FirmWithdrawalDetails
                                    closeModal={() => setWithdrawalInstructionsFormOpen(false)}
                                    editWithdrawalInstruction={editSelectedWithdrawalInstruction}
                                    firm={firm}
                                    {...props}
                                />
                            )}
                        />
                        <Route
                            exact
                            path='/admin/firms/:firmId'
                            render={props => (
                                <Grid
                                    alignItems='center'
                                    className={classes.noSelectedUser}
                                    container
                                    direction='column'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <Typography variant='body1'>{noWithdrawalInstructionSelectedMessage}</Typography>
                                </Grid>
                            )}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </GridSettingsContext.Provider>
    );
}